import { isRenderableAmountScale, parsePercentToAmountScale } from '../../../shared/lib/amount';
import { nextSortDirection, type SortDirection } from '../../../shared/lib/sort';
import type { Language } from '../../../shared/lib/language';
import type { ConsolidationState, Position, PositionSummary } from '../api/position';

export type PositionSortColumn =
  | 'instrument'
  | 'class'
  | 'quantity'
  | 'price'
  | 'value'
  | 'weight';

export interface PositionSort {
  column: PositionSortColumn;
  direction: SortDirection;
}

export const DEFAULT_POSITION_SORT: PositionSort = { column: 'weight', direction: 'descending' };

/**
 * The row as the reader sees it: the class label and the weight are rendered strings the
 * table already resolves, so the sort ranks what is on screen rather than a second,
 * differently-derived version of it.
 */
export interface PositionRow {
  position: Position;
  classLabel: string;
  weight: string | null;
}

/**
 * The sentence the table owes the reader about the percentages it is showing, or `null`
 * when it owes none.
 */
export type PositionRankingCaveat = 'weights-per-currency' | 'zero-total';

/**
 * What the table is actually showing: the order the rows are in, and what it owes the
 * reader about the percentages in the weight column.
 */
export interface PositionRanking {
  sort: PositionSort | null;
  caveat: PositionRankingCaveat | null;
}

export interface PositionRankingInput {
  rows: PositionRow[];
  consolidation: ConsolidationState;
  summary: PositionSummary | null | undefined;
  /** The column the reader picked, or `null` while they have picked none. */
  chosenSort: PositionSort | null;
}

/**
 * Whether the column can put these rows in an order at all.
 *
 * A sort over a column in which no row carries a comparable value is not a sort: the
 * comparator answers 0 for every pair and the rows come out in the order they went in.
 * A header reporting a direction for that is reporting a ranking that did not happen,
 * whether the column was chosen by the reader or opened by default.
 */
function columnRanksRows(rows: PositionRow[], column: PositionSortColumn): boolean {
  if (column === 'instrument' || column === 'class') return rows.length > 0;
  return rows.some((row) => amountScaleOf(row, column) != null);
}

/**
 * The order, the header's claim and the caveat, all read off the data on screen.
 *
 * These were three answers in three places, and each one was right about a different
 * moment: the opening order looked at the summary once, the header looked only at which
 * column was picked, and the caveat looked only for a rate outage. So a table could rank
 * nothing while its header pointed down, or decline to rank and explain nothing. The one
 * question asked here is whether the ranking **now on screen** is honest, and it is asked
 * again on every render, so a refresh that empties the weights takes the arrow with it.
 *
 * Two separate things make a weight ranking dishonest, and they are not the same fact:
 *
 * - **Different wholes.** When the consolidated NAV cannot resolve, b-cell-position falls
 *   back to each position's own currency NAV, so the column holds percentages that each
 *   sum to 100% per currency — 60% of a small book would outrank 45% of a large one under
 *   a header reading "% of portfolio". The figures are real and the reader may rank them
 *   deliberately; the table just does not do it unasked. So this decides the *opening*
 *   order, and the line above the rows says what the percentages are — a fact about the
 *   figures, which holds in whatever order they are shown.
 * - **Nothing to rank.** No row carries a weight that reads as a number — the summary
 *   failed, nothing was priced, or the book fully nets and a share of a zero total is
 *   undefined. Here the sort orders nothing whoever asked for it, so no header may claim
 *   it, and the zero-total case gets the same account of itself the allocation card gives.
 *
 * The two caveats read different `consolidation` values, so at most one is ever owed.
 *
 * `consolidation` is passed in rather than resolved here: it is the one answer the NAV
 * card, the donut and this table share, and reading it here would pull the API client
 * into a module that only compares rows.
 */
export function positionRanking({
  rows,
  consolidation,
  summary,
  chosenSort,
}: PositionRankingInput): PositionRanking {
  const requested = chosenSort ?? openingSortFor(consolidation);
  const sort = requested != null && columnRanksRows(rows, requested.column) ? requested : null;
  return { sort, caveat: rankingCaveat({ rows, consolidation, summary }) };
}

function openingSortFor(consolidation: ConsolidationState): PositionSort | null {
  return consolidation === 'rate-missing' ? null : DEFAULT_POSITION_SORT;
}

function rankingCaveat({
  rows,
  consolidation,
  summary,
}: Omit<PositionRankingInput, 'chosenSort'>): PositionRankingCaveat | null {
  // What each percentage measures against, which the rate outage decides and the sort does
  // not. Withholding this line while the reader ranks the column would take it away at the
  // one moment a cross-currency order is on screen, under a header reading "% of
  // portfolio" — the reader who ranks deliberately is the one who most needs to be told
  // that these figures do not share a whole.
  if (consolidation === 'rate-missing') return 'weights-per-currency';
  // A total the service resolved, and it is zero, so every share of it is undefined. The
  // allocation card already tells the reader this about the same response; a table that
  // quietly drops its ranking and says nothing leaves the two disagreeing.
  if (
    consolidation === 'consolidated'
    && summary?.consolidated_nav?.nav_amount === 0
    && !columnRanksRows(rows, 'weight')
  ) {
    return 'zero-total';
  }
  return null;
}

/**
 * The sort a click produces, from the sort the header is currently reporting — `null`
 * when it reports none, as on a portfolio whose weights cannot be ranked. The toggle
 * reads the claim on screen rather than the reader's last pick, so a click on the column
 * showing `descending` gives `ascending` even when that direction was never chosen. The
 * rule itself lives in `shared/lib/sort.ts`, so this table and the blotter cannot answer
 * a click differently.
 */
export function nextPositionSort(
  current: PositionSort | null,
  column: PositionSortColumn,
): PositionSort {
  const currentDirection = current?.column === column ? current.direction : null;
  return { column, direction: nextSortDirection(currentDirection) };
}

interface AmountScale {
  amount: bigint;
  scale: number;
}

function amountScaleOrNull(
  amount: number | null | undefined,
  scale: number | null | undefined,
): AmountScale | null {
  if (!isRenderableAmountScale(amount, scale)) return null;
  return { amount: BigInt(amount as number), scale: scale as number };
}

function amountScaleOf(row: PositionRow, column: PositionSortColumn): AmountScale | null {
  const { position } = row;
  switch (column) {
    case 'quantity':
      return amountScaleOrNull(position.symbol_amount, position.symbol_scale);
    case 'price':
      return amountScaleOrNull(position.current_price_amount, position.current_price_scale);
    case 'value':
      return amountScaleOrNull(position.total_amount, position.total_scale);
    default:
      return parsePercentToAmountScale(row.weight);
  }
}

function textOf(row: PositionRow, column: PositionSortColumn): string {
  if (column === 'class') return row.classLabel;
  return row.position.symbol || row.position.instrument_id;
}

/**
 * The column's pairs re-expressed at one shared scale, as exact integers.
 *
 * A ledger scale reaches 18 and an amount can already sit near the top of the range a
 * JavaScript number holds exactly, so `amount / 10 ** scale` can tie two figures that
 * differ (VEL-376 is the same range, met from the other side). BigInt carries the whole
 * value, and lifting every row to the widest scale in the column once means the sort
 * itself compares plain integers.
 *
 * What counts as no answer is decided per column, by what the reader is shown. Quantity,
 * price and value are printed through `formatAmountScale`, so `isRenderableAmountScale` —
 * the predicate that formatter asks — is what separates them: a row showing `—` sorts as
 * unknown rather than as some number nobody saw. The weight column prints the server's
 * own string verbatim and is bounded by nothing, so any weight that reads as a number
 * ranks as that number however large it is, and only an absent or non-numeric weight is
 * unknown. Ranking a printed figure last, among the rows that have no figure at all, is
 * the same class of untruth as ranking a dash as a number.
 */
function comparableAmounts(rows: PositionRow[], column: PositionSortColumn): Array<bigint | null> {
  const pairs = rows.map((row) => amountScaleOf(row, column));
  let widestScale = 0;
  for (const pair of pairs) {
    if (pair != null && pair.scale > widestScale) widestScale = pair.scale;
  }
  return pairs.map((pair) =>
    pair == null ? null : pair.amount * 10n ** BigInt(widestScale - pair.scale),
  );
}

/**
 * Unknowns sink to the bottom whichever way the column points. An absent price, an
 * unrepresentable total and a position the summary could not weigh are all "no answer",
 * and a direction flip must not float them to the top as if they were the smallest
 * holdings. A real zero is a real answer and sorts with the numbers.
 */
function compareWithUnknownsLast<T>(
  left: T | null,
  right: T | null,
  direction: SortDirection,
  compareKnown: (left: T, right: T) => number,
): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  const ascending = compareKnown(left, right);
  return direction === 'descending' ? -ascending : ascending;
}

const compareBigInts = (left: bigint, right: bigint) => (left < right ? -1 : left > right ? 1 : 0);

/**
 * Returns a new array; ties keep the order the server sent, because `Array.sort` is
 * stable and the comparator answers 0 for them.
 *
 * Text columns collate in the language the labels were chosen in, not in the browser's:
 * a `pt` console read in an `en-US` browser would otherwise order accented class labels
 * by English rules, which is not the order that console's own reader expects.
 */
export function sortPositionRows(
  rows: PositionRow[],
  sort: PositionSort,
  language: Language,
): PositionRow[] {
  const ordered = rows.map((row, index) => ({ row, index }));
  if (sort.column === 'instrument' || sort.column === 'class') {
    const texts = rows.map((row) => textOf(row, sort.column));
    const compareTexts = (left: string, right: string) => left.localeCompare(right, language);
    ordered.sort((left, right) =>
      compareWithUnknownsLast(texts[left.index], texts[right.index], sort.direction, compareTexts),
    );
  } else {
    const amounts = comparableAmounts(rows, sort.column);
    ordered.sort((left, right) =>
      compareWithUnknownsLast(
        amounts[left.index], amounts[right.index], sort.direction, compareBigInts,
      ),
    );
  }
  return ordered.map((entry) => entry.row);
}
